import { useEffect, useState, useCallback, Fragment } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useQuery } from "@tanstack/react-query";
import { handleAPIError } from "@/lib/errorHandler";
import { useReportDateRange } from "@/hooks/useDateRange";
import { fetchJSON } from "@/lib/api";
import { formatDate } from "@/lib/dateUtils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// ─── Stat Card ───────────────────────────────────────────────────────────────
function StatCard({
  label,
  value,
  accent = "#2563eb",
}: {
  label: string;
  value: string | number;
  accent?: string;
}) {
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        padding: "18px 24px",
        minWidth: 180,
        flex: "1 1 180px",
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: "0.08em",
          color: accent,
          textTransform: "uppercase",
          marginBottom: 8,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 26,
          fontWeight: 800,
          color: accent,
          fontFamily: "'Georgia', serif",
        }}
      >
        ₹{typeof value === "number" ? value.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : value}
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function DayCashReport() {
  const { fromDate, toDate, setFromDate, setToDate, isValidRange } =
    useReportDateRange("CURRENT_DAY");
  
  // Manually set dates to today on mount to ensure correct default values
  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    setFromDate(today);
    setToDate(today);
  }, [setFromDate, setToDate]);
  
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<any[]>([]);
  const [filterText, setFilterText] = useState("");
  const [showEntries, setShowEntries] = useState("All");
  const [expandedDate, setExpandedDate] = useState<string | null>(null);

  const filteredRows = rows.filter((r) => {
    const search = filterText.toLowerCase();
    if (!search) return true;
    return Object.values(r).some((v) => String(v).toLowerCase().includes(search));
  });

  const displayedRows =
    showEntries === "All"
      ? filteredRows
      : filteredRows.slice(0, parseInt(showEntries));

  const [totals, setTotals] = useState({
    meterSale: 0,
    lubSale: 0,
    recovery: 0,
    cashIn: 0,
    totalCash: 0,
    swipe: 0,
    credit: 0,
    expenses: 0,
    discount: 0,
    recoveryBank: 0,
    shortage: 0,
    handCash: 0,
  });
  const [errorMsg, setErrorMsg] = useState<string>("");
  const [shiftId, setShiftId] = useState<string>("ALL");

  const { data: shifts = [] } = useQuery({
    queryKey: ["/api/duty-shifts"],
    queryFn: async () => {
      const response = await fetchJSON("/api/duty-shifts");
      const result = await response.json();
      if (!result.ok) throw new Error(result.error || "Failed to fetch duty shifts");
      return result.rows || [];
    },
  });

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setErrorMsg("");
    try {
      const params = new URLSearchParams();
      params.append("from_date", fromDate);
      params.append("to_date", toDate);
      if (shiftId !== "ALL") params.append("shift_id", shiftId);

      const saleEntriesParams = new URLSearchParams();
      saleEntriesParams.append("from", fromDate);
      saleEntriesParams.append("to", toDate);
      if (shiftId !== "ALL") saleEntriesParams.append("shift_id", shiftId);

      const [
        guestRes, creditRes, lubeRes, expRes, recRes, meterRes,
        swipeRes, empCashRes, settlementsRes,
      ] = await Promise.all([
        fetchJSON(`/api/guest-sales?${params}`),
        fetchJSON(`/api/credit-sales?${params}`),
        fetchJSON(`/api/lubricant-sales?${params}`),
        fetchJSON(`/api/expenses?${params}`),
        fetchJSON(`/api/recoveries?${params}`),
        fetchJSON(`/api/sale-entries?${saleEntriesParams}`),
        fetchJSON(`/api/swipe-transactions?${params}`),
        fetchJSON(`/api/employee-cash-recovery?${params}`),
        fetchJSON('/api/day-settlements'),
      ]);
      const [
        guestData, creditData, lubeData, expData, recData, meterData,
        swipeData, empCashData, settlementsData,
      ] = await Promise.all([
        guestRes.json(), creditRes.json(), lubeRes.json(), expRes.json(),
        recRes.json(), meterRes.json(), swipeRes.json(), empCashRes.json(),
        settlementsRes.json(),
      ]);

      const normalizeDate = (d: any) => {
        if (!d) return "";
        const str = String(d);
        if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
        try {
          const parsed = new Date(str);
          if (!isNaN(parsed.getTime())) {
            return parsed.toISOString().split("T")[0];
          }
        } catch (e) {}
        return str.split("T")[0];
      };

      const map: Record<string, any> = {};
      const ensure = (d: string) => {
        const normalized = normalizeDate(d);
        return (map[normalized] ||= {
          date: normalized,
          meterSale: 0, lubSale: 0, recovery: 0, cashIn: 0, totalCash: 0,
          swipe: 0, credit: 0, expenses: 0, discount: 0, recoveryBank: 0,
          shortage: 0, handCash: 0, settlement: "Pending",
        });
      };

      (meterData.rows || []).forEach((m: any) => {
        if (shiftId !== "ALL" && m.shift_id !== shiftId) return;
        ensure(m.sale_date).meterSale += Number(m.net_sale_amount || 0);
      });
      (lubeData.rows || []).forEach((l: any) => {
        const r = ensure(l.sale_date);
        r.lubSale += Number(l.total_amount || 0);
        r.discount += Number(l.discount || 0);
      });
      (recData.rows || []).forEach((rv: any) => {
        const r = ensure(rv.recovery_date);
        const amt = Number(rv.received_amount || 0);
        const disc = Number(rv.discount || 0);
        if (rv.payment_mode === "Cash") r.recovery += amt;
        else r.recoveryBank += amt;
        r.discount += disc;
      });
      (expData.rows || []).forEach((e: any) => {
        ensure(e.expense_date).expenses += Number(e.amount || 0);
      });
      (creditData.rows || []).forEach((c: any) => {
        ensure(c.sale_date).credit += Number(c.total_amount || 0);
      });
      (swipeData.rows || []).forEach((s: any) => {
        ensure(s.transaction_date).swipe += Number(s.amount || 0);
      });
      (empCashData.rows || []).forEach((emp: any) => {
        ensure(emp.recovery_date).shortage += Number(emp.shortage_amount || 0);
      });
      (guestData.rows || []).forEach((g: any) => { ensure(g.sale_date); });

      const settlementsMap: Record<string, any> = {};
      (settlementsData.rows || []).forEach((s: any) => {
        const dateStr = s.settlement_date ? String(s.settlement_date).slice(0, 10) : "";
        if (dateStr) {
          settlementsMap[dateStr] = s;
        }
      });

      Object.values(map).forEach((r: any) => {
        const salesCash = r.meterSale + r.lubSale - r.credit - r.swipe;
        r.cashIn = Math.max(0, salesCash);
        r.totalCash = r.cashIn + r.recovery;
        r.handCash = r.totalCash - r.expenses;

        const settlement = settlementsMap[r.date];
        if (settlement) {
          r.settlement = "Settled";
          r.settlementDetails = settlement;
        } else {
          r.settlement = "Pending";
          r.settlementDetails = null;
        }
      });

      const arr = Object.values(map).sort((a, b) => a.date.localeCompare(b.date));
      setRows(arr);

      const t = arr.reduce(
        (acc, r: any) => ({
          meterSale: acc.meterSale + r.meterSale,
          lubSale: acc.lubSale + r.lubSale,
          recovery: acc.recovery + r.recovery,
          cashIn: acc.cashIn + r.cashIn,
          totalCash: acc.totalCash + r.totalCash,
          swipe: acc.swipe + r.swipe,
          credit: acc.credit + r.credit,
          expenses: acc.expenses + r.expenses,
          discount: acc.discount + r.discount,
          recoveryBank: acc.recoveryBank + r.recoveryBank,
          shortage: acc.shortage + r.shortage,
          handCash: acc.handCash + r.handCash,
        }),
        {
          meterSale: 0, lubSale: 0, recovery: 0, cashIn: 0, totalCash: 0,
          swipe: 0, credit: 0, expenses: 0, discount: 0, recoveryBank: 0,
          shortage: 0, handCash: 0,
        }
      );
      setTotals(t);
      setLoading(false);
    } catch (e: any) {
      const errorInfo = handleAPIError(e, "Day Cash Report");
      setErrorMsg(errorInfo.description);
      setRows([]);
      setLoading(false);
    }
  }, [fromDate, toDate, shiftId, isValidRange]);

  useEffect(() => {
    if (fromDate && toDate) {
     fetchReport();
    }
  }, [fromDate, toDate]);

  // ── styles ──────────────────────────────────────────────────────────────────
  const pageStyle: React.CSSProperties = {
    background: "#f0f2f5",
    minHeight: "100vh",
    padding: "0 0 40px",
    fontFamily: "'Segoe UI', system-ui, sans-serif",
  };

  const headerBarStyle: React.CSSProperties = {
    background: "#fff",
    borderBottom: "1px solid #e5e7eb",
    padding: "18px 32px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  };

  const iconBoxStyle: React.CSSProperties = {
    width: 48,
    height: 48,
    borderRadius: 10,
    background: "linear-gradient(135deg,#1d4ed8,#3b82f6)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 14,
    flexShrink: 0,
  };

  const formPanelStyle: React.CSSProperties = {
    background: "#fff",
    borderRadius: 14,
    border: "1px solid #e5e7eb",
    margin: "24px 32px",
    overflow: "hidden",
  };

  const formPanelHeaderStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "14px 24px",
    background: "linear-gradient(135deg,#1e40af 0%,#2563eb 60%,#3b82f6 100%)",
  };

  const formRowStyle: React.CSSProperties = {
    display: "flex",
    flexWrap: "wrap" as const,
    gap: 16,
    padding: "20px 24px",
    alignItems: "flex-end",
    background: "#fff",
  };

  const labelStyle: React.CSSProperties = {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.08em",
    color: "#374151",
    marginBottom: 6,
    display: "block",
  };

  const inputStyle: React.CSSProperties = {
    background: "#fff",
    border: "1px solid #d1d5db",
    borderRadius: 8,
    color: "#111827",
    height: 40,
    padding: "0 12px",
    fontSize: 14,
    outline: "none",
    width: "100%",
  };

  const fieldBox = (flex = "1 1 160px") => ({
    flex,
    minWidth: 140,
  });

  const sectionStyle: React.CSSProperties = {
    margin: "0 32px",
  };

  return (
    <div style={pageStyle}>
      {/* ── Page Header ── */}
      <div style={headerBarStyle}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <div style={iconBoxStyle}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="5" width="20" height="14" rx="2" />
              <path d="M2 10h20" />
            </svg>
          </div>
          <div>
            <div style={{ fontSize: 22, fontWeight: 800, color: "#111827", lineHeight: 1.2 }}>
              Day Cash Report
            </div>
            <div style={{ fontSize: 13, color: "#6b7280", marginTop: 2 }}>
              Daily Business &amp; Cash Management
            </div>
          </div>
        </div>

        {/* Add Sale Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                background: "#2563eb",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                padding: "10px 18px",
                fontWeight: 700,
                fontSize: 14,
                cursor: "pointer",
              }}
            >
              <PlusIcon className="" />
              Add Sale
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => (window.location.href = "/day-business/sale-entry")}>Sale Entry</DropdownMenuItem>
            <DropdownMenuItem onClick={() => (window.location.href = "/day-business/lubricants")}>Lubricant</DropdownMenuItem>
            <DropdownMenuItem onClick={() => (window.location.href = "/day-business/swipe")}>Swipe</DropdownMenuItem>
            <DropdownMenuItem onClick={() => (window.location.href = "/day-business/credit-sale")}>Credit Sale</DropdownMenuItem>
            <DropdownMenuItem onClick={() => (window.location.href = "/day-business/expenses")}>Expenses</DropdownMenuItem>
            <DropdownMenuItem onClick={() => (window.location.href = "/day-business/recovery")}>Recovery</DropdownMenuItem>
            <DropdownMenuItem onClick={() => (window.location.href = "/day-business/day-settlement")}>Day Settlement</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* ── Blue Filter/Search Panel ── */}
      <div style={formPanelStyle}>
        {/* Panel Header */}
        <div style={formPanelHeaderStyle}>
          <div style={{
            width: 32, height: 32, borderRadius: 8,
            background: "rgba(255,255,255,0.15)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
            </svg>
          </div>
          <span style={{ color: "#fff", fontWeight: 700, fontSize: 15 }}>Search &amp; Filter</span>
          <span style={{
            background: "rgba(255,255,255,0.2)", color: "#fff",
            fontSize: 11, fontWeight: 700, letterSpacing: "0.06em",
            padding: "3px 10px", borderRadius: 20, marginLeft: 4,
          }}>REPORT FILTERS</span>
        </div>

        {/* Form Row */}
        <div style={formRowStyle}>
          {/* From Date */}
          <div style={fieldBox("1 1 160px")}>
            <label style={labelStyle}>FROM DATE *</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => {
                const value = e.target.value;
                // Parse the date to ensure correct format (YYYY-MM-DD)
                const parts = value.split('-');
                if (parts.length === 3) {
                  // Browser returns YYYY-MM-DD, but locale might cause issues
                  // Ensure the year is correct (should be 2026, not 2025)
                  const [year, month, day] = parts.map(Number);
                  const currentDate = new Date();
                  const currentYear = currentDate.getFullYear();
                  
                  // If the parsed year is significantly different from current year, it might be a locale issue
                  if (Math.abs(year - currentYear) > 1) {
                    // Try to re-parse as DD-MM-YYYY
                    const [d, m, y] = parts;
                    const correctedDate = `${y}-${m}-${d}`;
                    setFromDate(correctedDate);
                  } else {
                    setFromDate(value);
                  }
                } else {
                  setFromDate(value);
                }
              }}
              style={inputStyle}
            />
          </div>

          {/* To Date */}
          <div style={fieldBox("1 1 160px")}>
            <label style={labelStyle}>TO DATE *</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => {
                const value = e.target.value;
                // Parse the date to ensure correct format (YYYY-MM-DD)
                const parts = value.split('-');
                if (parts.length === 3) {
                  // Browser returns YYYY-MM-DD, but locale might cause issues
                  // Ensure the year is correct (should be 2026, not 2025)
                  const [year, month, day] = parts.map(Number);
                  const currentDate = new Date();
                  const currentYear = currentDate.getFullYear();
                  
                  // If the parsed year is significantly different from current year, it might be a locale issue
                  if (Math.abs(year - currentYear) > 1) {
                    // Try to re-parse as DD-MM-YYYY
                    const [d, m, y] = parts;
                    const correctedDate = `${y}-${m}-${d}`;
                    setToDate(correctedDate);
                  } else {
                    setToDate(value);
                  }
                } else {
                  setToDate(value);
                }
              }}
              style={inputStyle}
            />
          </div>

          {/* Shift */}
          <div style={fieldBox("1 1 160px")}>
            <label style={labelStyle}>SHIFT</label>
            <select
              value={shiftId}
              onChange={(e) => setShiftId(e.target.value)}
              style={{ ...inputStyle, appearance: "none" as const }}
            >
              <option value="ALL">All Shifts</option>
              {shifts.map((s: any) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>

          {/* Filter text */}
          <div style={fieldBox("1 1 200px")}>
            <label style={labelStyle}>SEARCH RECORDS</label>
            <div style={{ position: "relative" }}>
              <input
                type="text"
                placeholder="Type to filter..."
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                style={{ ...inputStyle, paddingLeft: 36 }}
              />
              <svg style={{ position: "absolute", left: 10, top: 11, opacity: 0.45 }} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
              </svg>
            </div>
          </div>

          {/* Search + Clear Buttons */}
          <div style={{ flex: "0 0 auto", alignSelf: "flex-end", display: "flex", gap: 8 }}>
            <button
              onClick={fetchReport}
              disabled={loading}
              style={{
                background: loading ? "#fb923c" : "#f97316",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                padding: "0 20px",
                fontWeight: 700,
                fontSize: 14,
                cursor: loading ? "not-allowed" : "pointer",
                height: 40,
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
              </svg>
              {loading ? "Searching..." : "Search"}
            </button>
            <button
              onClick={() => setFilterText("")}
              style={{
                background: "#fff",
                color: "#374151",
                border: "1px solid #d1d5db",
                borderRadius: 8,
                padding: "0 18px",
                fontWeight: 600,
                fontSize: 14,
                cursor: "pointer",
                height: 40,
              }}
            >
              Clear
            </button>
          </div>
        </div>

        {errorMsg && (
          <div style={{ color: "#dc2626", fontSize: 13, padding: "0 24px 16px" }}>
            ⚠ {errorMsg}
          </div>
        )}
      </div>

      {/* ── Table Section ── */}
      <div style={sectionStyle}>
        <div style={{
          background: "#fff",
          borderRadius: 14,
          border: "1px solid #e5e7eb",
          overflow: "hidden",
        }}>
          {/* Table toolbar */}
          <div style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "14px 20px",
            borderBottom: "1px solid #f3f4f6",
            flexWrap: "wrap",
            gap: 10,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 13, color: "#6b7280", fontWeight: 500 }}>Show</span>
              <select
                value={showEntries}
                onChange={(e) => setShowEntries(e.target.value)}
                style={{
                  border: "1px solid #d1d5db", borderRadius: 6,
                  padding: "4px 10px", fontSize: 13, color: "#374151",
                  background: "#fff",
                }}
              >
                <option value="All">All</option>
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
              </select>
              <span style={{ fontSize: 13, color: "#6b7280" }}>entries</span>
            </div>

            <div style={{ display: "flex", gap: 8 }}>
              <button style={{
                border: "1.5px solid #EF4444", color: "#DC2626",
                background: "#fff", borderRadius: 6, padding: "5px 16px",
                fontSize: 13, fontWeight: 700, cursor: "pointer",
              }}>CSV</button>
              <button style={{
                border: "1.5px solid #22C55E", color: "#16A34A",
                background: "#fff", borderRadius: 6, padding: "5px 16px",
                fontSize: 13, fontWeight: 700, cursor: "pointer",
              }}>PDF</button>
              <button
                onClick={fetchReport}
                style={{
                  border: "1px solid #e5e7eb", color: "#6b7280",
                  background: "#fff", borderRadius: 6, padding: "5px 10px",
                  cursor: "pointer", fontSize: 16,
                }}
                title="Refresh"
              >↻</button>
            </div>
          </div>

          {/* Table */}
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#f8fafc" }}>
                  {[
                    "S.No", "Date", "Meter Sale(₹)", "Lub Sale(₹)", "Recovery(₹)",
                    "Cash In(₹)", "Total Cash(₹)", "Swipe(₹)", "Credit(₹)",
                    "Expenses(₹)", "Discount(₹)", "Recovery Bank(₹)", "Shortage(₹)",
                    "Hand Cash(₹)", "Settlement", "Pictures",
                  ].map((h, i) => (
                    <th
                      key={h}
                      style={{
                        padding: "11px 12px",
                        textAlign: i >= 2 && i <= 13 ? "right" : "left",
                        fontWeight: 700,
                        fontSize: 11,
                        letterSpacing: "0.05em",
                        color: "#374151",
                        borderBottom: "2px solid #e5e7eb",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {displayedRows.length === 0 ? (
                  <tr>
                    <td colSpan={16} style={{ textAlign: "center", padding: "48px 0", color: "#9ca3af", fontSize: 14 }}>
                      No data available in table
                    </td>
                  </tr>
                ) : (
                  displayedRows.map((r, i) => {
                    const isExpanded = expandedDate === r.date;
                    const sd = r.settlementDetails;
                    const openingHandcash = sd ? (sd.opening_balance || 0) : 0;
                    const cashToBank = sd ? (sd.cash_to_bank || 0) : 0;
                    const closingBalance = sd ? (sd.closing_balance || 0) : 0;
                    const shortage = sd ? (sd.shortage || 0) : 0;
                    // Formula: Cash In Hand = Meter + Lubricant - Credit - Swipe - Expenses - Cash to Bank + Opening + Recovery
                    const expectedCash = r.meterSale + r.lubSale - r.credit - r.swipe - r.expenses - cashToBank + openingHandcash + r.recovery;

                    return (
                      <Fragment key={r.date}>
                        <tr
                          style={{ borderBottom: "1px solid #f3f4f6" }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = "#f8fafc")}
                          onMouseLeave={(e) => (e.currentTarget.style.background = "")}
                        >
                          <td style={{ padding: "10px 12px", color: "#6b7280" }}>{i + 1}</td>
                          <td style={{ padding: "10px 12px", fontWeight: 600, whiteSpace: "nowrap", color: "#111827" }}>
                            {formatDate(r.date)}
                          </td>
                          <td style={{ padding: "10px 12px", textAlign: "right" }}>{r.meterSale.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                          <td style={{ padding: "10px 12px", textAlign: "right" }}>{r.lubSale.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                          <td style={{ padding: "10px 12px", textAlign: "right", color: "#059669", fontWeight: 600 }}>{r.recovery.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                          <td style={{ padding: "10px 12px", textAlign: "right" }}>{r.cashIn.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                          <td style={{ padding: "10px 12px", textAlign: "right", fontWeight: 700 }}>{r.totalCash.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                          <td style={{ padding: "10px 12px", textAlign: "right" }}>{r.swipe.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                          <td style={{ padding: "10px 12px", textAlign: "right" }}>{r.credit.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                          <td style={{ padding: "10px 12px", textAlign: "right", color: "#dc2626" }}>{r.expenses.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                          <td style={{ padding: "10px 12px", textAlign: "right" }}>{r.discount.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                          <td style={{ padding: "10px 12px", textAlign: "right" }}>{r.recoveryBank.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                          <td style={{ padding: "10px 12px", textAlign: "right", color: "#ef4444" }}>{r.shortage.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                          <td style={{ padding: "10px 12px", textAlign: "right", fontWeight: 700, color: "#1d4ed8" }}>{r.handCash.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                          <td style={{ padding: "10px 12px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                              <span style={{
                                background: r.settlement === "Pending" ? "#fef3c7" : "#d1fae5",
                                color: r.settlement === "Pending" ? "#92400e" : "#065f46",
                                borderRadius: 20, padding: "3px 10px", fontSize: 11, fontWeight: 700,
                                display: "inline-block"
                              }}>
                                {r.settlement}
                              </span>
                              <button
                                type="button"
                                onClick={() => setExpandedDate(isExpanded ? null : r.date)}
                                style={{
                                  width: 24,
                                  height: 24,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  borderRadius: "50%",
                                  background: "#dbeafe",
                                  border: "none",
                                  color: "#2563eb",
                                  cursor: "pointer",
                                  fontSize: 10,
                                  fontWeight: "bold",
                                  padding: 0,
                                  outline: "none"
                                }}
                                title="View template details"
                              >
                                {isExpanded ? "▼" : "▶"}
                              </button>
                            </div>
                          </td>
                          <td style={{ padding: "10px 12px", color: "#9ca3af" }}>—</td>
                        </tr>
                        {isExpanded && (
                          <tr style={{ background: "#f8fafc" }}>
                            <td colSpan={16} style={{ padding: "0" }}>
                              <div style={{
                                background: "linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)",
                                color: "#fff",
                                padding: "20px 24px",
                                borderBottom: "1.5px solid #e5e7eb"
                              }}>
                                {/* Header */}
                                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                                  <div style={{ width: 4, height: 24, borderRadius: 2, background: "#93c5fd" }} />
                                  <span style={{ fontWeight: 700, fontSize: 16, letterSpacing: "0.025em" }}>
                                    Settlement {formatDate(r.date)}
                                  </span>
                                </div>
                                
                                {/* Grid */}
                                <div style={{
                                  display: "grid",
                                  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                                  gap: 12,
                                  marginBottom: 16
                                }}>
                                  {/* Item Cards */}
                                  {[
                                    { label: "Opening Handcash", value: openingHandcash, prefix: "₹" },
                                    { label: "Meter Sale", value: r.meterSale, prefix: "₹", color: "#6ee7b7" },
                                    { label: "Lubricant Sale", value: r.lubSale, prefix: "₹", color: "#6ee7b7" },
                                    { label: "Credit (deducted)", value: r.credit, prefix: "− ₹", color: "#fdba74" },
                                    { label: "Swipe / non-cash (deducted)", value: r.swipe, prefix: "− ₹", color: "#7dd3fc" },
                                    { label: "Total Sale (Meter + Lub)", value: r.meterSale + r.lubSale, prefix: "₹" },
                                    { label: "Expenses", value: r.expenses, prefix: "₹", color: "#fca5a5" },
                                    { label: "Credit Recovery (added)", value: r.recovery, prefix: "+ ₹", color: "#6ee7b7" },
                                    { label: "Cash to Bank", value: cashToBank, prefix: "₹", color: "#6ee7b7" },
                                    { label: "Shortage / Excess", value: shortage, prefix: "₹", color: shortage < 0 ? "#f87171" : "#6ee7b7" },
                                    { label: "Day Closing Bal. cash", value: closingBalance, prefix: "₹" },
                                  ].map((item, idx) => (
                                    <div key={idx} style={{
                                      background: "rgba(255,255,255,0.1)",
                                      border: "1px solid rgba(255,255,255,0.05)",
                                      borderRadius: 8,
                                      padding: "10px 16px"
                                    }}>
                                      <div style={{ color: "#93c5fd", fontSize: 11, marginBottom: 2 }}>{item.label}</div>
                                      <div style={{
                                        fontWeight: 600,
                                        fontSize: 14,
                                        color: item.color || "#fff"
                                      }}>
                                        {item.prefix}{Number(item.value || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                      </div>
                                    </div>
                                  ))}
                                </div>

                                {/* Summary Bar */}
                                <div style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  background: "rgba(255,255,255,0.15)",
                                  border: "1px solid rgba(255,255,255,0.2)",
                                  borderRadius: 12,
                                  padding: "12px 20px",
                                  flexWrap: "wrap",
                                  gap: 12
                                }}>
                                  <span style={{ color: "#bfdbfe", fontSize: 12 }}>
                                    Cash In Hand = Meter + Lubricant − Credit − Swipe − Expenses − Cash to Bank + Opening + Recovery
                                  </span>
                                  <span style={{ fontSize: 20, fontWeight: 800, color: "#6ee7b7" }}>
                                    ₹{Number(expectedCash || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </span>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })
                )}

                {/* Totals row */}
                {displayedRows.length > 0 && (
                  <tr style={{ background: "#1e40af" }}>
                    <td colSpan={2} style={{ padding: "12px", fontWeight: 800, color: "#fff", fontSize: 13 }}>TOTAL</td>
                    {[
                      totals.meterSale, totals.lubSale, totals.recovery, totals.cashIn,
                      totals.totalCash, totals.swipe, totals.credit, totals.expenses,
                      totals.discount, totals.recoveryBank, totals.shortage, totals.handCash,
                    ].map((v, i) => (
                      <td key={i} style={{ padding: "12px", textAlign: "right", fontWeight: 800, color: "#fff", fontSize: 13 }}>
                        {v.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                    ))}
                    <td style={{ padding: "12px" }}></td>
                    <td style={{ padding: "12px" }}></td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <path d="M5 12h14" />
      <path d="M12 5v14" />
    </svg>
  );
}
